/**
 * Presentational settings cards shared by every delivery-step tab: a "General"
 * card (status field, action, enabled toggle) and an optional "Batch Settings"
 * card (numeric RPA tuning knobs). Parameterised by `idPrefix` so each tab keeps
 * distinct element ids.
 */
import { ToggleSwitch } from "@risalabs_frontend_org/oasis-ui-kit";
import { Select, TextInput } from "risa-oasis-ui_v2";
import { TrackStatusSettings } from "../../../../../data-model/deliveryStepsConfig";
import { DELIVERY_STEP_FIELD_CONFIG } from "./deliveryStepFields";

const ACTION_OPTIONS = [
  { value: "fetch", label: "Fetch" },
  { value: "fetch_and_run", label: "Fetch and Run" },
  { value: "store_stats", label: "Store Stats" },
];

const STATUS_FIELD_OPTIONS = DELIVERY_STEP_FIELD_CONFIG.fields.map((f) => ({
  value: f.value,
  label: f.label,
}));

const SETTING_FIELDS: { key: keyof TrackStatusSettings; label: string }[] = [
  { key: "batch_size", label: "Batch Size" },
  { key: "max_retries", label: "Max Retries" },
  { key: "batch_stagger_seconds", label: "Batch Stagger (seconds)" },
  { key: "rpa_timeout_seconds", label: "RPA Timeout (seconds)" },
  { key: "distributed_batch_size", label: "Distributed Batch Size" },
];

interface StepSettingsCardProps {
  idPrefix: string;
  enabled: boolean;
  action: string;
  statusField: string;
  settings: TrackStatusSettings;
  /** When false, the Batch Settings card is hidden (e.g. Stats Storage). */
  showBatchSettings?: boolean;
  onEnabledChange: (value: boolean) => void;
  onActionChange: (value: string) => void;
  onStatusFieldChange: (value: string) => void;
  onSettingChange: (
    key: keyof TrackStatusSettings,
    value: number | undefined,
  ) => void;
}

const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-4 rounded-lg border border-primaryGray-16 p-4">
    <div className="text-sm font-semibold text-primaryGray-1">{title}</div>
    {children}
  </div>
);

/** General + (optional) batch-settings cards for a delivery step. */
const StepSettingsCard = ({
  idPrefix,
  enabled,
  action,
  statusField,
  settings,
  showBatchSettings = true,
  onEnabledChange,
  onActionChange,
  onStatusFieldChange,
  onSettingChange,
}: StepSettingsCardProps) => (
  <div className="flex flex-col gap-4">
    <SectionCard title="General">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Select
          id={`${idPrefix}-status-field`}
          label="Status Source Field"
          options={STATUS_FIELD_OPTIONS}
          value={statusField}
          defaultValue={statusField}
          onOptionChange={(data: { value: string }) =>
            onStatusFieldChange(data.value)
          }
          placeholder="Select status field..."
        />
        <Select
          id={`${idPrefix}-action`}
          label="Action"
          options={ACTION_OPTIONS}
          value={action}
          defaultValue={action}
          onOptionChange={(data: { value: string }) =>
            onActionChange(data.value)
          }
          placeholder="Select action..."
        />
      </div>
      <div className="flex flex-row items-center justify-between gap-7 rounded-md border border-primaryGray-14 p-4">
        <div className="flex flex-col gap-1">
          <div className="text-sm">Enabled</div>
          <div className="text-sm text-primaryGray-8">
            When disabled, this delivery step is skipped during processing.
          </div>
        </div>
        <ToggleSwitch
          id={`${idPrefix}-enabled`}
          defaultChecked={enabled}
          onChangeEmit={() => onEnabledChange(!enabled)}
        />
      </div>
    </SectionCard>

    {showBatchSettings ? (
      <SectionCard title="Batch Settings">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {SETTING_FIELDS.map(({ key, label }) => (
            <TextInput
              key={key}
              id={`${idPrefix}-setting-${key}`}
              label={label}
              type="number"
              placeholder="0"
              defaultValue={settings[key] != null ? String(settings[key]) : ""}
              onChange={(data: { value: string }) =>
                onSettingChange(
                  key,
                  data.value === "" ? undefined : Number(data.value),
                )
              }
            />
          ))}
        </div>
      </SectionCard>
    ) : null}
  </div>
);

export default StepSettingsCard;
